import {AppRoutes} from '../enums/routes';
import {Roles} from './x-enums/roles';
import type {AuthenticationService} from './authentication.service';

/**
 * **The** rule for "which lockout screen does this session owe, if any" (#558).
 *
 * Three places have to agree on it and used to state it three times:
 *
 * * `AuthGuard` — pins a restricted session to its screen and bounces a no-longer-restricted one off it.
 * * `ErrorInterceptor` — turns a gate's 403 into a navigation to the screen that can lift it.
 * * `GateRefreshService` — routes a session that a live refresh has just newly restricted.
 *
 * They disagreed once already and the comment in `ErrorInterceptor` records what it cost: the interceptor
 * sending a session to the forms screen while the guard bounced it straight to the two-factor one is a
 * pointless hop and a flash of the wrong screen. With three gates that failure mode is no longer a nuisance,
 * it is a loop, so the ordering lives here and nowhere else.
 *
 * ### The order, and why it is this one
 *
 * `two-factor → forms → contact`.
 *
 * 1. **Account security (#282) first.** It is the tightest gate — a session that has not proven its second
 *    factor has proven only a password — and every other screen's own calls are refused until it is satisfied,
 *    so sending such a session anywhere else produces a screen that cannot work. It is also the only gate that
 *    is *not* employee-only: a company-wide `AccountSecurityLevel` restricts a ShardAdmin, a CompanyOwner and
 *    a PlatformOwner identically.
 * 2. **Forms (#404) before contact (#558).** Both are employee-only, both are self-serviceable, and neither
 *    blocks the other's screen — so the order is a tie-break rather than a dependency. Forms is the older,
 *    more consequential requirement (a tax form beats a messenger handle), and *some* fixed order is what
 *    makes a doubly-restricted session resolve one screen at a time instead of ping-ponging.
 *
 * 3. **Employment (#864) last.** An employee whose employment has not started yet waits on a screen that only
 *    tells them the date. Every other gate names something they can do now, and the employment gate opens all
 *    of their routes, so those come first and the waiting screen is what is left when nothing else is owed.
 *
 * A session restricted by two gates therefore works through them in that order: satisfying the two-factor gate
 * re-issues a token that still says `forms_gate = restricted`, and the next navigation pins it to the forms
 * screen — no special "what's next" logic anywhere.
 */

/** The four screens a restricted session can be pinned to. */
export type GateLockoutRoute = AppRoutes.TwoFactorRequired | AppRoutes.FormsRequired | AppRoutes.ContactRequired
  | AppRoutes.EmploymentPending;

/**
 * The gate verdicts a decision needs, decoupled from where they were read.
 *
 * A flat value object rather than the session token, deliberately: `AuthenticationService` already owns the
 * "absent claim reads as ok" defaulting for all three claims (load-bearing — every session stored in a browser
 * predates whichever claim shipped last), and re-deriving it from raw claim strings here would be a second
 * copy of that rule, in the one place where getting it wrong locks people out.
 */
export interface GateRestrictions {
  /** `acct_sec = restricted` (#282). Applies to every kind of session. */
  accountSecurityRestricted: boolean;
  /** `forms_gate = restricted` (#404). Employee sessions only. */
  formsRestricted: boolean;
  /** `contact_gate = restricted` (#558). Employee sessions only. */
  contactRestricted: boolean;
  /** `employment_gate = restricted` (#864): the employment has not started yet. Employee sessions only. */
  employmentPending?: boolean;
  /**
   * Whether the session holds {@link Roles.Employee}.
   *
   * The forms and contact gates are scoped to employees server-side, so a non-employee never carries a
   * restricting claim — but reading that as "therefore no check is needed" is exactly the assumption that ages
   * badly. Checking it here costs nothing and means a stray claim can only ever *fail to* restrict, never
   * strand a manager on an employee screen they have no way off.
   */
  isEmployee: boolean;
}

/**
 * The screen this session owes, or `null` when it owes none.
 *
 * Pure — no router, no service, no side effect — so the ordering can be pinned by a spec that does not have to
 * stand up a guard.
 */
export function lockoutRouteFor(restrictions: GateRestrictions): GateLockoutRoute | null {
  if (restrictions.accountSecurityRestricted) return AppRoutes.TwoFactorRequired;
  if (!restrictions.isEmployee) return null;
  if (restrictions.formsRestricted) return AppRoutes.FormsRequired;
  if (restrictions.contactRestricted) return AppRoutes.ContactRequired;
  if (restrictions.employmentPending) return AppRoutes.EmploymentPending;
  return null;
}

/**
 * {@link lockoutRouteFor} for the **live session**, read off `AuthenticationService`.
 *
 * A free function taking the service rather than a getter on it, for one practical reason worth keeping: every
 * caller's spec provides the service as a `useValue` mock of plain properties, and a getter defined on the
 * real class is simply absent from such an object. Reading the three published getters here keeps those mocks
 * — and therefore the existing guard/interceptor specs — honest about what the rule actually consumes.
 */
export function lockoutRouteForSession(auth: AuthenticationService): GateLockoutRoute | null {
  return lockoutRouteFor({
    accountSecurityRestricted: auth.isAccountSecurityRestricted === true,
    formsRestricted: auth.isFormsRestricted === true,
    contactRestricted: auth.isContactRestricted === true,
    employmentPending: auth.isEmploymentPending === true,
    isEmployee: auth.userValue?.permissions?.includes(Roles.Employee) === true
  });
}

/**
 * Whether this URL is one of the lockout screens that poll their own gate (two-factor, forms, contact).
 *
 * Used by `GateRefreshService` to stay off those screens entirely — each polls its own gate status, which is
 * both fresher and narrower than a whole-session refresh, and a background token swap under a running wizard
 * is a way to lose a half-finished remediation for no gain.
 *
 * The employment screen (#864) is not in this list. It has no status of its own to poll: the
 * whole-session refresh (push, and the alarm on the start instant) is the only thing that can lift it.
 *
 * Prefix-matched against the path only, exactly as `AuthGuard` does it: these routes take no parameters today,
 * but a `?returnUrl=` or a future child route must not make the check miss.
 */
export function isLockoutPath(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return LOCKOUT_ROUTES.some(route => path === `/${route}` || path.startsWith(`/${route}/`));
}

/** The self-polling lockout screens, in gate order. Exported so a spec can assert the list rather than restate it. */
export const LOCKOUT_ROUTES: readonly GateLockoutRoute[] = [
  AppRoutes.TwoFactorRequired,
  AppRoutes.FormsRequired,
  AppRoutes.ContactRequired
];

/**
 * The screens an employment-pending session (#864) may visit besides its own waiting screen: the forms and
 * contact screens, which the server opens to it. The guard lets such a session stay on them rather than pinning
 * it back to the waiting screen, as long as there is something to do there (a `review` verdict).
 */
export const EMPLOYMENT_PENDING_MAY_VISIT: readonly GateLockoutRoute[] = [
  AppRoutes.FormsRequired,
  AppRoutes.ContactRequired
];
